import * as React from 'react'
import styled from 'styled-components'
import InfoIcon from 'assets/info-icon.svg'
import theme from 'theme'
import useCart from 'hooks/account/cart/useCart'
import CartItem from 'theme/molecules/account/CartItem'
import usePromotionCodes from 'hooks/account/cart/usePromotionCodes'
import CartPromotionItem from 'theme/molecules/account/CartPromotionItem'
import useTranslations from 'hooks/useTranslations'
import { isItemChannelRestricted } from 'hooks/account/utils'
import useAccount from 'hooks/account/useAccount'
import partition from 'utils/partition-array'

type Props = {
  onPartnerLinkClick: () => void
}

export default function CartItems(props: Props) {
  const t = useTranslations<'templates-Cart'>()
  const cart = useCart()
  const promotions = usePromotionCodes()
  const channel = useAccount().channel
  const [ownItems, partnerItems] = React.useMemo(
    () => partition(cart.data.items, (item) => !item.isPartnerItem),
    [cart.data.items]
  )

  const displayAdditionalItems = partnerItems.length > 0

  return (
    <Wrapper className="CartItems" $hasOnlyPartnerItems={ownItems.length === 0}>
      <div className="articles">
        {ownItems.map((item, i) => (
          <CartItem
            channelBlocked={isItemChannelRestricted(item, channel)}
            key={item.sw6Uuid + i}
            item={item}
            updateItem={(amount) => cart.updateItem(item.cartItemId, amount)}
          />
        ))}
        {displayAdditionalItems && (
          <div
            className="marketplace-item-container"
            data-cy-state="has-marketplace-items"
          >
            <div className="additional-items-headline">
              {t('shipping_by_partner')}
            </div>
            {
              <InfoIcon
                data-cy-handler="partner-info-icon"
                className="clickableIcon"
                onClick={props.onPartnerLinkClick}
              />
            }
          </div>
        )}
        {displayAdditionalItems &&
          partnerItems.map((item, i) => (
            <CartItem
              channelBlocked={isItemChannelRestricted(item, channel)}
              key={item.sw6Uuid + i}
              item={item}
              updateItem={(amount) => cart.updateItem(item.cartItemId, amount)}
            />
          ))}
        {promotions.items.map((promoItem, i) => (
          <CartPromotionItem
            onRemove={() =>
              promoItem.cartItemId && cart.removeItem(promoItem.cartItemId)
            }
            key={`promo-${i}`}
            promotionData={{
              ...promoItem,
              reduction: promoItem.reduction || cart.data.deliveryReduction,
              type: promoItem.priceDefinitionType || '',
              value:
                promoItem.priceDefinitionPercentage ||
                promoItem.priceDefinitionAbsolute ||
                0
            }}
            data-cy-state="show-cart-promotion-item"
          />
        ))}
        <div className="info">
          <InfoIcon />
          {t('cart_info_text_below')}
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $hasOnlyPartnerItems: boolean }>`
  > .articles {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('m')};

    > .marketplace-item-container {
      margin-top: ${(p) => (p.$hasOnlyPartnerItems ? 0 : theme.spacing('m'))};
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: ${theme.spacing('xs')};

      > .additional-items-headline {
        ${theme.ty('rc-2xl', 'bold', 'rc-3xl')}
      }
      > svg {
        max-width: 18px;
        max-height: 18px;

        circle {
          fill: ${theme.colors.shade.b1};
        }
      }
    }
    > .info {
      display: flex;
      margin-top: 0;
      gap: ${theme.spacing('xxs')};
      color: ${theme.colors.accent.blue};
      align-items: center;
      ${theme.ty('r-xs', null, 'r-s')}

      @media (min-width: ${theme.ms.MD}px) {
        margin-top: ${theme.spacing('m')};
      }
      > svg {
        width: 15px;
        height: 15px;
        circle {
          fill: ${theme.colors.accent.blue};
        }
      }
    }
  }
`
